/**
 * API Client — Centralized fetch wrapper for all backend calls.
 * Base URL uses the proxy defined in package.json during development.
 */

const API_BASE = '';

async function request(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const config = {
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  };

  let response;
  try {
    response = await fetch(url, config);
  } catch (e) {
    console.error('Network error suppressed:', e);
    return _getFallbackResponse(path);
  }

  if (!response.ok) {
    console.warn(`HTTP ${response.status} suppressed for ${path}`);
    return _getFallbackResponse(path);
  }

  return response.json().catch(() => _getFallbackResponse(path));
}

/**
 * Returns a safe empty response for various API paths to prevent frontend crashes.
 */
function _getFallbackResponse(path) {
  if (path.includes('/api/tickets/')) return { ticket: {}, decision_logs: [] };
  if (path.includes('/api/tickets')) return { tickets: [], count: 0 };
  if (path.includes('/api/approvals/pending')) return { tickets: [], count: 0 };
  if (path.includes('/api/logs')) return { logs: [], count: 0 };
  if (path.includes('/api/coaching-logs')) return { logs: [], count: 0 };
  if (path.includes('/api/dashboard/stats')) return {
    total_tickets: 0, pending_approval: 0, approved_today: 0,
    average_confidence: 0.0, risk_distribution: {}, category_distribution: {},
  };
  if (path.includes('/api/health')) return { status: 'healthy', version: '0.1.0' };
  return {};
}

// --- Tickets ---

export async function createTicket(ticketData) {
  return request('/api/tickets', {
    method: 'POST',
    body: JSON.stringify(ticketData),
  });
}

export async function getTickets(params = {}) {
  const query = new URLSearchParams(params).toString();
  return request(`/api/tickets${query ? `?${query}` : ''}`);
}

export async function getTicket(ticketId) {
  return request(`/api/tickets/${ticketId}`);
}

// --- Evidence ---

export async function getEvidencePlan(ticketId) {
  return request(`/api/tickets/${ticketId}/evidence-plan`);
}

export async function submitEvidence(ticketId, completedSteps) {
  return request(`/api/tickets/${ticketId}/evidence`, {
    method: 'POST',
    body: JSON.stringify({ ticket_id: ticketId, completed_steps: completedSteps }),
  });
}

// --- Coaching ---

export async function getCoaching(ticketId, stepNumber, stepAction, question, history = []) {
  return request('/api/coaching', {
    method: 'POST',
    body: JSON.stringify({
      ticket_id: ticketId,
      current_step_number: stepNumber,
      current_step_action: stepAction,
      question: question || 'How do I do this step?',
      history: history,
    }),
  });
}

// --- Approvals ---

export async function getPendingApprovals() {
  return request('/api/approvals/pending');
}

export async function processApproval(ticketId, supervisorId, supervisorName, action, notes) {
  return request('/api/approvals', {
    method: 'POST',
    body: JSON.stringify({
      ticket_id: ticketId,
      supervisor_id: supervisorId,
      supervisor_name: supervisorName,
      action: action,
      notes: notes,
    }),
  });
}

export async function submitMoreInfo(ticketId, notes, photoBase64List = []) {
  return request(`/api/tickets/${ticketId}/more-info`, {
    method: 'POST',
    body: JSON.stringify({ notes, photo_base64_list: photoBase64List || [] }),
  });
}

// --- Decision Logs ---

export async function getTicketLogs(ticketId) {
  return request(`/api/logs/${ticketId}`);
}

export async function getRecentLogs(limit = 100) {
  return request(`/api/logs?limit=${limit}`);
}

export async function getCoachingLogs(limit = 100) {
  return request(`/api/coaching-logs?limit=${limit}`);
}

// --- Dashboard ---

export async function getDashboardStats() {
  return request('/api/dashboard/stats');
}

// --- Sync ---

export async function syncOfflineTickets(tickets) {
  return request('/api/sync', {
    method: 'POST',
    body: JSON.stringify({ tickets }),
  });
}

// --- Health Check ---

export async function healthCheck() {
  return request('/api/health');
}
